import type {
  BrandContext,
  BrandEnvironment,
  BrandKey,
  EmailBrand,
  EnabledAuthProvider,
} from '@/lib/brand/types';
import {
  DEFAULT_NYLA_FONT_FAMILY,
  DEFAULT_NYLA_PRIMARY_COLOR,
  DEFAULT_NYLA_SECONDARY_COLOR,
  getBrandFontFamilyStack,
  normalizeBrandFontFamily,
  normalizeHexColor,
} from '@/lib/brand/presentation';

type EnvShape = Partial<Record<string, string | undefined>>;
type HeaderValue = string | string[] | undefined;
type HeaderRecord = Record<string, HeaderValue>;

const EXPLORE_DEFAULT_ORIGIN = 'https://www.learnwithexplore.com';
const EXPLORE_SENDER_EMAIL = 'support@exploresupport.com';
const EXPLORE_SENDER_NAME = 'MindLabs at Explore';
const EXPLORE_APP_NAME = 'MindLabs';
const NYLA_APP_NAME = 'Nyla Nova STEM Missions';
const EXPLORE_LOGO_PATH = '/images/MindlabsEmailLogo.png';
const NYLA_LOGO_PATH = '/images/NylaNovaStemTransparent4.png';
export const DEFAULT_NATIVE_GAME_REDIRECT_URL =
  'https://app.learnwithexplore.com/game/p';

const EXPLORE_DEFAULT_HOSTS = [
  'www.learnwithexplore.com',
  'learnwithexplore.com',
  'dev.learnwithexplore.com',
  'staging.learnwithexplore.com',
  'localhost',
  'localhost:3000',
  '127.0.0.1',
  '127.0.0.1:3000',
] as const;

const EXPLORE_AUTH_PROVIDERS: EnabledAuthProvider[] = [
  'credentials',
  'student-room-code',
  'clever',
  'clever-sync',
];

const NYLA_AUTH_PROVIDERS: EnabledAuthProvider[] = [
  'credentials',
  'student-room-code',
];

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const splitList = (value?: string) =>
  (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

export const normalizeHost = (value?: string | null) => {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(
      trimmed.includes('://') ? trimmed : `https://${trimmed}`
    );
    return parsed.host.replace(/\.$/, '');
  } catch {
    return trimmed.split('/')[0]?.replace(/\.$/, '') || null;
  }
};

export const normalizeOrigin = (value?: string | null) => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    const host = normalizeHost(value);
    return host ? `https://${host}` : null;
  }
};

const normalizeAbsoluteUrl = (value?: string | null) => {
  if (!value) return null;

  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const hostFromOrigin = (value?: string | null) => {
  const origin = normalizeOrigin(value);
  return origin ? new URL(origin).host.toLowerCase() : null;
};

const normalizeSupportEmail = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase();
  return normalized && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
    ? normalized
    : null;
};

const getHeaderValue = (headers: HeaderRecord | Headers, name: string) => {
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    return headers.get(name) ?? undefined;
  }

  const record = headers as HeaderRecord;
  const value = record[name] ?? record[name.toLowerCase()];
  return Array.isArray(value) ? value[0] : value;
};

export const resolveRequestHostFromHeaders = (
  headers: HeaderRecord | Headers
) =>
  normalizeHost(
    getHeaderValue(headers, 'x-forwarded-host') ??
      getHeaderValue(headers, 'host') ??
      null
  );

export const getBrandEnvironment = (
  env: EnvShape = process.env
): BrandEnvironment => {
  const raw = (
    env.NEXT_PUBLIC_ENV_STATUS ||
    env.VERCEL_TARGET_ENV ||
    env.VERCEL_ENV ||
    ''
  ).toLowerCase();

  if (raw.includes('prod')) return 'production';
  if (raw.includes('stag')) return 'staging';
  if (raw.includes('dev')) return 'dev';
  if (raw.includes('preview')) return 'preview';
  return env.VERCEL_URL ? 'preview' : 'local';
};

const getExploreOrigin = (env: EnvShape) =>
  stripTrailingSlash(
    normalizeOrigin(env.NEXT_PUBLIC_BASE_URL) ?? EXPLORE_DEFAULT_ORIGIN
  );

const getNylaOrigin = (env: EnvShape, nylaHosts: string[]) => {
  const configuredOrigin = normalizeOrigin(env.NYLA_PUBLIC_ORIGIN);
  if (configuredOrigin) {
    return stripTrailingSlash(configuredOrigin);
  }

  const firstHost = nylaHosts[0];
  return firstHost ? `https://${firstHost}` : `${getExploreOrigin(env)}/nyla`;
};

const getExploreNativeGameRedirectUrl = (env: EnvShape) =>
  normalizeAbsoluteUrl(env.EXPLORE_NATIVE_GAME_REDIRECT_URL) ??
  DEFAULT_NATIVE_GAME_REDIRECT_URL;

const getNylaNativeGameRedirectUrl = (env: EnvShape) =>
  normalizeAbsoluteUrl(env.NYLA_NATIVE_GAME_REDIRECT_URL) ??
  getExploreNativeGameRedirectUrl(env);

const isLocalHost = (host: string) =>
  host.startsWith('localhost') || host.startsWith('127.0.0.1');

const originForHost = (host: string | null, canonicalOrigin: string) => {
  if (!host) {
    return canonicalOrigin;
  }

  const normalizedCanonicalOrigin = normalizeOrigin(canonicalOrigin);
  const protocol = isLocalHost(host)
    ? 'http:'
    : normalizedCanonicalOrigin
      ? new URL(normalizedCanonicalOrigin).protocol
      : 'https:';

  return `${protocol}//${host}`;
};

const originsForHosts = (hosts: Iterable<string>, canonicalOrigin: string) =>
  Array.from(hosts).map((host) => originForHost(host, canonicalOrigin));

const getConfiguredHosts = ({
  env,
  key,
  origins,
  defaults = [],
  includeVercelUrl = false,
}: {
  env: EnvShape;
  key: string;
  origins: Array<string | null | undefined>;
  defaults?: readonly string[];
  includeVercelUrl?: boolean;
}) => {
  const hosts = new Set<string>();

  defaults.forEach((host) => {
    const normalized = normalizeHost(host);
    if (normalized) hosts.add(normalized);
  });

  splitList(env[key]).forEach((host) => {
    const normalized = normalizeHost(host);
    if (normalized) hosts.add(normalized);
  });

  origins.forEach((origin) => {
    const normalized = hostFromOrigin(origin);
    if (normalized) hosts.add(normalized);
  });

  if (includeVercelUrl && env.VERCEL_URL) {
    const normalized = normalizeHost(env.VERCEL_URL);
    if (normalized) hosts.add(normalized);
  }

  return hosts;
};

const buildEmailBrand = ({
  appName,
  requestOrigin,
  logoSrc,
  supportEmail = EXPLORE_SENDER_EMAIL,
  primaryColor,
  secondaryColor,
  fontFamily,
}: {
  appName: string;
  requestOrigin: string;
  logoSrc: string;
  supportEmail?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
}): EmailBrand => ({
  senderName: EXPLORE_SENDER_NAME,
  senderEmail: EXPLORE_SENDER_EMAIL,
  replyToEmail: supportEmail,
  appName,
  logoSrc,
  homeUrl: requestOrigin,
  supportCopy: `Need help? Reply to ${supportEmail} and our team will help.`,
  ...(primaryColor ? { primaryColor } : {}),
  ...(secondaryColor ? { secondaryColor } : {}),
  ...(fontFamily
    ? { fontFamily: getBrandFontFamilyStack(fontFamily, 'nyla') }
    : {}),
});

export const buildBrandContext = ({
  brandKey,
  environment,
  canonicalOrigin,
  requestOrigin,
  requestHost,
  appName,
  enabledAuthProviders,
  analyticsEnabled,
  collectionsEnabled = false,
  isApprovedHost,
  partnerKey,
  partnerId,
  logoPath,
  primaryColor,
  secondaryColor,
  fontFamily,
  supportEmail,
  nativeGameRedirectUrl,
  allowedRedirectOrigins,
}: {
  brandKey: BrandKey;
  environment: BrandEnvironment;
  canonicalOrigin: string;
  requestOrigin: string;
  requestHost: string | null;
  appName: string;
  enabledAuthProviders: EnabledAuthProvider[];
  analyticsEnabled: boolean;
  collectionsEnabled?: boolean;
  isApprovedHost: boolean;
  partnerKey?: string;
  partnerId?: string;
  logoPath?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  supportEmail?: string;
  nativeGameRedirectUrl?: string;
  allowedRedirectOrigins?: string[];
}): BrandContext => {
  const isNylaBrand = brandKey === 'nyla';
  const publicOrigin = requestOrigin;
  const resolvedLogoPath =
    logoPath ?? (brandKey === 'nyla' ? NYLA_LOGO_PATH : EXPLORE_LOGO_PATH);
  const logoSrc = normalizeAbsoluteUrl(resolvedLogoPath)
    ? resolvedLogoPath
    : `${requestOrigin}${resolvedLogoPath}`;
  const resolvedPrimaryColor = isNylaBrand
    ? (normalizeHexColor(primaryColor) ?? DEFAULT_NYLA_PRIMARY_COLOR)
    : undefined;
  const resolvedSecondaryColor = isNylaBrand
    ? (normalizeHexColor(secondaryColor) ?? DEFAULT_NYLA_SECONDARY_COLOR)
    : undefined;
  const resolvedFontFamily = isNylaBrand
    ? (normalizeBrandFontFamily(fontFamily) ?? DEFAULT_NYLA_FONT_FAMILY)
    : undefined;
  const resolvedSupportEmail =
    normalizeSupportEmail(supportEmail) ?? EXPLORE_SENDER_EMAIL;
  const redirectOrigins = Array.from(
    new Set([requestOrigin, canonicalOrigin, ...(allowedRedirectOrigins ?? [])])
  );

  return {
    brandKey,
    environment,
    appName,
    requestHost,
    requestOrigin,
    canonicalOrigin,
    publicOrigin,
    allowedRedirectOrigins: redirectOrigins,
    enabledAuthProviders,
    analyticsEnabled,
    collectionsEnabled,
    logoSrc,
    ...(resolvedPrimaryColor ? { primaryColor: resolvedPrimaryColor } : {}),
    ...(resolvedSecondaryColor
      ? { secondaryColor: resolvedSecondaryColor }
      : {}),
    ...(resolvedFontFamily ? { fontFamily: resolvedFontFamily } : {}),
    ...(isNylaBrand || supportEmail
      ? { supportEmail: resolvedSupportEmail }
      : {}),
    emailBrand: buildEmailBrand({
      appName,
      requestOrigin,
      logoSrc,
      supportEmail: resolvedSupportEmail,
      primaryColor: resolvedPrimaryColor,
      secondaryColor: resolvedSecondaryColor,
      fontFamily: resolvedFontFamily,
    }),
    isApprovedHost,
    ...(nativeGameRedirectUrl ? { nativeGameRedirectUrl } : {}),
    ...(partnerKey ? { partnerKey } : {}),
    ...(partnerId ? { partnerId } : {}),
  };
};

export const resolveBrandContextForHost = (
  rawHost?: string | null,
  env: EnvShape = process.env
): BrandContext => {
  const environment = getBrandEnvironment(env);
  const host = normalizeHost(rawHost);
  const exploreOrigin = getExploreOrigin(env);
  const nylaHosts = splitList(env.NYLA_APPROVED_HOSTS)
    .map(normalizeHost)
    .filter((value): value is string => Boolean(value));
  const nylaOrigin = getNylaOrigin(env, nylaHosts);
  const exploreHosts = getConfiguredHosts({
    env,
    key: 'EXPLORE_APPROVED_HOSTS',
    origins: [exploreOrigin],
    defaults: EXPLORE_DEFAULT_HOSTS,
    includeVercelUrl: true,
  });
  const approvedNylaHosts = getConfiguredHosts({
    env,
    key: 'NYLA_APPROVED_HOSTS',
    origins: [env.NYLA_PUBLIC_ORIGIN],
  });

  if (host && approvedNylaHosts.has(host)) {
    const nylaRequestOrigin = originForHost(host, nylaOrigin);
    return buildBrandContext({
      brandKey: 'nyla',
      environment,
      canonicalOrigin: nylaOrigin,
      requestOrigin: nylaRequestOrigin,
      requestHost: host,
      appName: NYLA_APP_NAME,
      enabledAuthProviders: NYLA_AUTH_PROVIDERS,
      analyticsEnabled: env.NYLA_ANALYTICS_ENABLED === 'true',
      isApprovedHost: true,
      partnerKey: 'nyla',
      nativeGameRedirectUrl: getNylaNativeGameRedirectUrl(env),
      allowedRedirectOrigins: [
        ...originsForHosts(approvedNylaHosts, nylaOrigin),
        ...splitList(env.NYLA_ALLOWED_REDIRECT_ORIGINS),
      ],
    });
  }

  const exploreRequestOrigin =
    host && exploreHosts.has(host)
      ? originForHost(host, exploreOrigin)
      : exploreOrigin;

  return buildBrandContext({
    brandKey: 'explore',
    environment,
    canonicalOrigin: exploreOrigin,
    requestOrigin: exploreRequestOrigin,
    requestHost: host,
    appName: EXPLORE_APP_NAME,
    enabledAuthProviders: EXPLORE_AUTH_PROVIDERS,
    analyticsEnabled:
      environment === 'production' && exploreOrigin === EXPLORE_DEFAULT_ORIGIN,
    collectionsEnabled: true,
    isApprovedHost: Boolean(host && exploreHosts.has(host)),
    nativeGameRedirectUrl: getExploreNativeGameRedirectUrl(env),
    allowedRedirectOrigins: [
      ...originsForHosts(exploreHosts, exploreOrigin),
      ...splitList(env.EXPLORE_ALLOWED_REDIRECT_ORIGINS),
    ],
  });
};

export const getDefaultBrandContext = (env: EnvShape = process.env) =>
  resolveBrandContextForHost(hostFromOrigin(getExploreOrigin(env)), env);
